import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inbox, channelWa, billing, flows, isApiError } from '@repletoryreach/sdk-api';
import type { CannedReply, ConversationMessage, ConversationSummary } from '@repletoryreach/sdk-api/inbox';
import { useInboxEvent, useRealtime } from '@repletoryreach/sdk-realtime';
import { useDebounce, useElementSize, useInterval, useMediaQuery, useTimezone } from '@repletoryreach/sdk-utils/hooks';
import { useAuth } from '@repletoryreach/sdk-auth';
import { Button, Card, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useCredits } from '../../lib/credits';
import { useT, type Translate } from '../../i18n/I18nProvider';
import { useMetaBilling } from '../../lib/meta-billing';
import { usePersistedState } from '../../lib/usePersistedState';
import { useFillViewport } from '../../lib/useFillViewport';
import { LockIndicator } from './components/LockIndicator';
import { useNow } from './components/SlaBadge';
import { CannedReplyPicker, useCannedReplyMatches } from './components/CannedReplyPicker';
import { openTransferModal, useAgentOptions, type AgentOption } from './components/TransferModal';
import { useTeams } from './components/useTeams';
import { ConversationList } from './components/ConversationList';
import { QueueFilters, QUEUES, type Queue } from './components/QueueFilters';
import { MessageThread, type PendingMessage } from './components/MessageThread';
import { MessageDetails } from './components/MessageDetails';
import { useSessionWindow, ClockIcon } from './components/SessionWindow';
import { ThreadHeader } from './components/ThreadHeader';
import { Composer } from './components/Composer';
import { BlockCustomerModal } from './components/BlockCustomerModal';
import { NewBookingModal } from '../bookings/components/NewBookingModal';
import { CreateTicketModal } from '../tickets/components/CreateTicketModal';
import { linkedTicketKey, useLinkedTicket } from './components/LinkedTicket';
import { useOrgTimezone } from '../bookings/lib/data';
import {
  ConversationSidebar,
  DEFAULT_SECTIONS,
  sidebarSectionId,
  type SidebarSectionId
} from './components/ConversationSidebar';
import './inbox.css';

/** Rows per page. `meta.next_cursor` walks past this. */
const PAGE_SIZE = 30;

/** Below this the console is one pane at a time, list OR thread. */
const NARROW = '(max-width: 900px)';   // i18n-ignore: CSS media query
/** Above this the queue column gets a little wider. */
const WIDE = '(min-width: 1200px)';    // i18n-ignore: CSS media query
/**
 * The settings sidebar is a column beside the thread only when the thread's
 * column is at least this wide, which leaves the conversation about 520px
 * after the sidebar's 376. Below it the same sidebar opens as a drawer.
 *
 * Measured rather than a media query: the console's own navigation can be
 * open or folded, so a 1440px window has room for the column in one case and
 * would crush the conversation to a strip in the other.
 */
const DOCK_SIDEBAR_MIN = 900;

export default function Inbox() {
  const t = useT();
  const credits = useCredits();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const now = useNow(15_000);
  const { status: rtStatus } = useRealtime();
  const narrow = useMediaQuery(NARROW);
  const wide = useMediaQuery(WIDE);
  const threadRef = useRef<HTMLElement>(null);
  const threadSize = useElementSize(threadRef);
  const docked = threadSize.width >= DOCK_SIDEBAR_MIN;
  const { user } = useAuth();
  const fill = useFillViewport();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Queue selection survives a reload: an agent who works one tab works it all
  // day.
  const [queue, setQueue] = usePersistedState<Queue>('tenant.inbox.queue', 'all');
  const [stateFilter, setStateFilter] = usePersistedState('tenant.inbox.state', '');
  const [unassignedOnly, setUnassignedOnly] = usePersistedState('tenant.inbox.unassigned', false);
  // The team filter: '' any, 'none' no team, else a team's id. Applied only while the organisation has teams,
  // so a remembered choice for a team since removed cannot empty the queue with no control to undo it.
  const teams = useTeams();
  const [teamPick, setTeamFilter] = usePersistedState('tenant.inbox.team', '');
  const teamFilter = teams.length > 0 && (teamPick === 'none' || teams.some((x) => x.id === teamPick)) ? teamPick : '';
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pages, setPages] = useState(1);

  /*
   * §4 item 7: `contacts/view[id]` has always linked to
   * `/inbox?conversation={id}` and this page never read the parameter, so
   * every one of those links landed on an unselected inbox.
   */
  useEffect(() => {
    const wanted = searchParams.get('conversation');
    if (wanted && wanted !== selectedId) setSelectedId(wanted);
  }, [searchParams, selectedId]);

  const selectConversation = useCallback((id: string) => {
    setSelectedId(id);
    // Keep the URL shareable, without stacking history entries per click.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('conversation', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /**
   * The tab and the filter panel fold into one query.
   *
   * Three of the four tabs are state filters and one is an assignment filter,
   * so `queueFilter` returns whichever it is and the panel's own state filter
   * and unassigned toggle layer on top. An explicit state in the panel wins
   * over the tab's: it is the more specific of the two, and it is the one the
   * agent just chose.
   */
  const queueFilter = useCallback((q: Queue): Record<string, unknown> => {
    if (q === 'open') return { state: 'open' };
    if (q === 'resolved') return { state: 'resolved' };
    if (q === 'mine') return user ? { agent_user_id: user.id } : {};
    return {};
  }, [user]);

  const listOpts = useMemo(() => ({
    limit: PAGE_SIZE * pages,
    ...queueFilter(queue),
    ...(stateFilter ? { state: stateFilter } : {}),
    ...(unassignedOnly ? { agent_user_id: null } : {}),
    ...(teamFilter ? { team_id: teamFilter === 'none' ? null : teamFilter } : {}),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {})
  }), [pages, stateFilter, unassignedOnly, teamFilter, queue, queueFilter, debouncedSearch]);

  const conversations = useQuery({
    queryKey: ['conversations', listOpts],
    queryFn: () => inbox.listConversations(listOpts),
    /*
     * §4 item 18: this polled every 15 s on top of a live socket that already
     * pushes `conversation.updated`. The poll's only real job is catching a
     * dropped connection, so it runs only when the socket is not connected.
     */
    refetchInterval: rtStatus === 'connected' ? false : 15_000,
    placeholderData: (prev) => prev
  });

  /*
   * The tab counts.
   *
   * `meta.total` only ever describes the filter that was sent, so the four
   * numbers need four asks. Each fetches a single row and reads the total off
   * the envelope, which is the cheapest honest way to get them: showing the
   * current tab's total against all four tabs would be a lie on three of them.
   */
  const counts = useQuery({
    queryKey: ['conversations', 'counts', user?.id ?? null],
    queryFn: async () => {
      const totals = await Promise.all(
        QUEUES.map((q) => inbox.listConversations({ limit: 1, ...queueFilter(q) })
          .then((r) => r.meta?.total)
          .catch(() => undefined))
      );
      return Object.fromEntries(QUEUES.map((q, i) => [q, totals[i]])) as Partial<Record<Queue, number>>;
    },
    staleTime: 15_000
  });

  const messages = useQuery({
    queryKey: ['conversation', selectedId, 'messages'],
    enabled: !!selectedId,
    queryFn: () => inbox.listMessages(selectedId!, { limit: 100 })
  });
  const { options: agents } = useAgentOptions();

  useInboxEvent('conversation.updated', (e) => {
    qc.invalidateQueries({ queryKey: ['conversations'] });
    qc.invalidateQueries({ queryKey: ['conversation', e.conversation_id, 'messages'] });
  });
  useInboxEvent('conversation.assigned', () => {
    qc.invalidateQueries({ queryKey: ['conversations'] });
  });
  useInboxEvent('conversation.resolved', (e) => {
    qc.invalidateQueries({ queryKey: ['conversations'] });
    qc.invalidateQueries({ queryKey: ['conversation', e.conversation_id] });
  });

  useEffect(() => {
    if (!selectedId) return;
    const tmr = setTimeout(() => {
      inbox.markConversationRead(selectedId).then(() =>
        qc.invalidateQueries({ queryKey: ['conversations'] })
      );
    }, 1000);
    return () => clearTimeout(tmr);
  }, [selectedId, qc]);

  // A new filter starts at page one; leaving the old page count would ask the
  // server for 90 rows of a list the agent has not scrolled yet.
  useEffect(() => { setPages(1); }, [queue, stateFilter, unassignedOnly, teamFilter, debouncedSearch]);

  const items: ConversationSummary[] = conversations.data?.data ?? [];
  const total = conversations.data?.meta?.total;
  const selected = items.find((c) => c.id === selectedId) ?? null;
  const msgs: ConversationMessage[] = messages.data?.data ?? [];
  const sortedMessages = useMemo(() => [...msgs].reverse(), [msgs]);

  const hasMore = total !== undefined && items.length < total;
  const filtered = queue !== 'all' || !!stateFilter || unassignedOnly || !!teamFilter || !!debouncedSearch.trim();

  function clearFilters() {
    setQueue('all');
    setStateFilter('');
    setUnassignedOnly(false);
    setTeamFilter('');
    setSearch('');
  }

  // On a phone the list and the thread take turns; there is no room for both.
  const showThread = !narrow || !!selected;
  const showList = !narrow || !selected;

  /*
   * No PageHeader. The app bar already names the page, and on a workbench the
   * title plus a sentence of description cost about 90px of the one thing this
   * screen is short of: room for the queue and the thread. The reference has
   * neither.
   */
  return (
    <div className="rr-inbox2-page" ref={fill.ref} style={fill.height ? { height: fill.height } : undefined}>
      <div className={`rr-inbox2${wide ? ' rr-inbox2--wide' : ''}${narrow ? ' rr-inbox2--narrow' : ''}`}>
        {showList && (
          <aside className="rr-inbox2__list">
            <QueueFilters
              queue={queue}
              onQueue={setQueue}
              state={stateFilter}
              onState={setStateFilter}
              unassignedOnly={unassignedOnly}
              onUnassignedOnly={setUnassignedOnly}
              search={search}
              onSearch={setSearch}
              team={teamFilter}
              onTeam={setTeamFilter}
              teams={teams}
              counts={counts.data}
              onNewMessage={() => navigate('/inbox/compose')}
            />
            <ConversationList
              items={items}
              selectedId={selectedId}
              onSelect={selectConversation}
              loading={conversations.isPending}
              error={conversations.error}
              now={now}
              total={total}
              filtered={filtered}
              onClearFilters={clearFilters}
              onAttachNumber={() => navigate('/numbers')}
              onLoadMore={hasMore ? () => setPages((p) => p + 1) : undefined}
              loadingMore={conversations.isFetching}
            />
          </aside>
        )}

        {showThread && (
          <main className="rr-inbox2__thread" ref={threadRef}>
            {!selected && (
              <div className="rr-inbox2__placeholder">
                <Card withBorder>
                  <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('inbox.select')}</Text>
                </Card>
              </div>
            )}
            {selected && (
              <ConversationPane
                key={selected.id}
                conversation={selected}
                messages={sortedMessages}
                loading={messages.isPending}
                error={messages.error}
                agents={agents}
                timezone={timezone}
                now={now}
                docked={docked}
                narrow={narrow}
                onBack={() => { setSelectedId(null); }}
              />
            )}
          </main>
        )}
      </div>
    </div>
  );
}

function lockErrorMessage(e: unknown, t: (k: string) => string): string {
  if (isApiError(e) && e.status === 409) {
    const body = e.body as { holder_name?: string | null } | null;
    return `${t('inbox.lockedBy')} ${body?.holder_name ?? t('inbox.anotherAgent')}`;
  }
  return e instanceof Error ? e.message : t('inbox.lockFailed');
}

/**
 * Turns a send failure into something an agent can act on.
 *
 * §4 item 3: this rendered `e.message`, which for a 402 is the string
 * "HTTP 402". `services/channel-wa` now puts a real sentence on both bodies, so
 * `fromErrorBody` lifts it onto `ApiError.message`; the codes are still matched
 * here so the copy is translated rather than English-from-the-server.
 */
function sendErrorMessage(e: unknown, t: Translate): string {
  if (isApiError(e)) {
    if (e.code === 'INSUFFICIENT_CREDITS') return t('inbox.send.noCredits');
    if (e.code === 'PHONE_NOT_FOUND') return t('inbox.send.numberInactive');
    // The business's own WhatsApp account after a move to a plan without it (5 October 2026).
    if (e.code === channelWa.OWN_ACCOUNT_NOT_ON_PLAN) return t('inbox.send.ownAccountNotOnPlan');
    // WhatsApp's daily limit on new people: on our shared account, this business's share of it.
    if (channelWa.isNumberDailyLimit(e)) {
      const d = e.body.error;
      return d.shared
        ? t('inbox.send.dailyLimit.shared', { share: d.share ?? d.limit ?? 0 })
        : t('inbox.send.dailyLimit', { limit: d.limit ?? 0 });
    }
    if (e.message && !/^HTTP \d+$/.test(e.message)) return e.message;
  }
  return e instanceof Error && !/^HTTP \d+$/.test(e.message) ? e.message : t('inbox.sendFailed');
}

/** A ticket's subject from the chat: the client's last words, on one line and not too long. */
function ticketSubjectFrom(messages: ConversationMessage[]): string {
  const withText = messages.filter((m) => m.body?.trim());
  const last = [...withText].reverse().find((m) => m.direction === 'inbound') ?? withText[withText.length - 1];
  const text = (last?.body ?? '').replace(/\s+/g, ' ').trim();
  return text.length > 120 ? `${text.slice(0, 117).trimEnd()}…` : text;
}

/**
 * "/" anywhere in the trailing word, not just at the very start of an empty
 * field (§4 item 6): `Thanks! /closing` now opens the picker too.
 */
const SLASH_QUERY = /(?:^|\s)\/(\S*)$/;

function ConversationPane({
  conversation,
  messages,
  loading,
  error,
  agents,
  timezone,
  now,
  docked,
  narrow,
  onBack
}: {
  conversation: ConversationSummary;
  messages: ConversationMessage[];
  loading: boolean;
  error: unknown;
  agents: AgentOption[];
  timezone: string;
  now: number;
  /** Whether the settings sidebar is a column (true) or a drawer. */
  docked: boolean;
  narrow: boolean;
  onBack: () => void;
}) {
  const t = useT();
  // Who Meta bills for this conversation's number: a billing refusal reads differently on the business's own account.
  const metaPayer = useMetaBilling().payerOf(conversation.phone_number_id);
  const credits = useCredits();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  /*
   * §4 item 13: the pane is keyed on the conversation id, so switching rows
   * remounted it and silently threw away a half-typed reply. Drafts are held
   * per conversation and persisted, so they also survive a reload.
   */
  const [drafts, setDrafts] = usePersistedState<Record<string, string>>('tenant.inbox.drafts', {});
  const reply = drafts[conversation.id] ?? '';
  const setReply = useCallback((value: string) => {
    setDrafts((prev) => {
      const next = { ...prev };
      if (value) next[conversation.id] = value;
      else delete next[conversation.id];
      return next;
    });
  }, [conversation.id, setDrafts]);

  const [dismissedFor, setDismissedFor] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingMessage[]>([]);
  /*
   * The settings sidebar.
   *
   * Docked, whether it is open is remembered: an agent who folds it away to
   * read wide wants it folded on the next conversation too. As a drawer it
   * always starts closed, because a remembered "open" would throw a sheet over
   * every conversation opened on a laptop. Each section remembers whether it
   * was left open, over the defaults.
   */
  const [sidebarOpen, setSidebarOpen] = usePersistedState('tenant.inbox.sidebar', true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sectionState, setSectionState] = usePersistedState<Partial<Record<SidebarSectionId, boolean>>>(
    'tenant.inbox.sidebar.sections', {}
  );
  const sections = { ...DEFAULT_SECTIONS, ...sectionState };
  const toggleSection = useCallback((id: SidebarSectionId) => {
    setSectionState((prev) => ({ ...prev, [id]: !{ ...DEFAULT_SECTIONS, ...prev }[id] }));
  }, [setSectionState]);
  const sidebarId = `conversation-settings-${conversation.id}`;
  /*
   * Which message the details panel is showing.
   *
   * Held by id and looked up again on each render rather than kept as a copy
   * of the row, so a receipt arriving while the panel is open moves the ticks
   * on the bubble and the timeline in the panel together.
   */
  const [detailsForId, setDetailsForId] = useState<string | null>(null);
  const detailsFor = messages.find((m) => m.id === detailsForId) ?? null;
  // A conversation that no longer holds the selected message closes the panel
  // rather than leaving it describing something off screen.
  useEffect(() => { if (detailsForId && !detailsFor) setDetailsForId(null); }, [detailsForId, detailsFor]);

  const detail = useQuery({
    queryKey: ['conversation', conversation.id, 'detail'],
    queryFn: () => inbox.getConversation(conversation.id),
    refetchInterval: 10_000
  });
  // What this contact has cost, from the attributed ledger (sends, bot
  // replies, tokens, inbound). Keyed by contact rather than conversation, so
  // an earlier resolved thread with the same person counts too.
  // A Messenger or Instagram thread has no phone number, so there is nothing
  // to total spend against: the meter attributes those to the channel account.
  const spendMsisdn = conversation.contact_msisdn;
  const spendQ = useQuery({
    queryKey: ['usage', 'sources', 'contact', spendMsisdn],
    queryFn: () => billing.getUsageSources('contact', [spendMsisdn!]),
    enabled: Boolean(spendMsisdn),
    refetchInterval: 30_000,
    retry: false
  });
  const spendRow = spendQ.data?.data[0];
  const spend = spendQ.isSuccess
    ? { amount: credits(spendRow?.amount_minor ?? 0, spendRow?.currency ?? 'ZAR'), entries: spendRow?.entries ?? 0 }
    : null;
  const lock = detail.data?.data.lock ?? null;
  const botPaused = detail.data?.data.bot_paused ?? conversation.bot_paused ?? false;
  const botResumesAt = detail.data?.data.bot_resumes_at ?? conversation.bot_resumes_at ?? null;
  // Take over and hand back (4 October 2026): who has this chat, and from which bot.
  const takenOverBy = detail.data?.data.taken_over_by_user_id ?? conversation.taken_over_by_user_id ?? null;
  const takenOverFrom = detail.data?.data.taken_over_from ?? conversation.taken_over_from ?? null;
  const takenByMe = !!takenOverBy && takenOverBy === user?.id;
  const myName = user?.display_name || user?.email || null;
  const blockInfo = detail.data?.data.block ?? null;
  const blocked = detail.data?.data.blocked ?? conversation.blocked ?? false;
  const [blockOpen, setBlockOpen] = useState(false);
  // Book for this person from the chat (7 October 2026): the booking is linked to the conversation.
  const [bookOpen, setBookOpen] = useState(false);
  const orgTimezone = useOrgTimezone();
  // A ticket for this conversation (7 October 2026): the chip shows the one it is linked to.
  const [ticketOpen, setTicketOpen] = useState(false);
  const linkedTicket = useLinkedTicket(conversation.id);
  const lockedByOther = !!lock && !lock.is_mine;
  const stateCode = detail.data?.data.state_code ?? conversation.state_code ?? 'open';
  const closed = stateCode === 'resolved' || stateCode === 'archived';
  const holderName = user?.display_name ?? user?.email ?? undefined;

  /*
   * §4 item 19: this invalidated `['conversation', id]`, which is a PREFIX of
   * the messages, notes and tags keys, so every lock, unlock and state change
   * refetched all three. The detail query now lives under its own leaf.
   */
  const refreshDetail = () => {
    qc.invalidateQueries({ queryKey: ['conversation', conversation.id, 'detail'] });
    qc.invalidateQueries({ queryKey: ['conversations'] });
  };

  const [activeIdx, setActiveIdx] = useState(0);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const listId = `canned-picker-${conversation.id}`;

  const slashMatch = SLASH_QUERY.exec(reply);
  const pickerOpen = !!slashMatch && dismissedFor !== reply;
  const pickerQuery = slashMatch?.[1] ?? '';
  const { matches, isLoading: pickerLoading } = useCannedReplyMatches(pickerQuery, pickerOpen);
  useEffect(() => { setActiveIdx(0); }, [pickerQuery]);

  const sessionWindow = useSessionWindow(messages, now);
  // A closed window rejects free text at Meta, so offering the composer is
  // offering a reply that cannot be sent.
  // Email has no window at all (4 October 2026): the server answers open, always. Nor has website chat (8 October
  // 2026): a reply there waits until the visitor next opens the chat, so the composer stays.
  const isEmail = conversation.channel === 'email';
  const hasWindow = conversation.channel !== 'email' && conversation.channel !== 'web' && conversation.channel !== 'voice';
  const windowClosed = hasWindow && !sessionWindow.open && !sessionWindow.neverOpened;

  /*
   * Which channel this thread is on decides how a reply goes out.
   *
   * WhatsApp keeps the path it has always had, down to the idempotency key:
   * straight to channel-wa's send, with the agent's own token. Messenger and
   * Instagram go through the inbox, which knows the thread, holds the window
   * and forwards to the Meta adapter. Two paths rather than one because the
   * WhatsApp one is proven and there is nothing to gain from moving it.
   */
  const isMeta = conversation.channel === 'messenger' || conversation.channel === 'instagram';
  // A Page whose connection Facebook ended (16 October 2026): nothing typed here would reach the customer, so the
  // reply box gives way to what happened and the way to fix it. The same list Channels shows, cached under its key.
  const metaAccounts = useQuery({
    queryKey: ['meta-accounts'],
    queryFn: () => flows.listMetaAccounts(),
    enabled: isMeta,
    staleTime: 60_000
  });
  const metaAccount = isMeta ? metaAccounts.data?.data.find((a) => a.id === conversation.channel_account_id) : undefined;
  const pageBroken = !!metaAccount?.broken_since;
  // The website chat (3 October 2026) also replies through the inbox, which hands it to the chat bubble.
  // So does email (4 October 2026): the inbox hands the reply to the email service, threaded under the last email.
  const viaInbox = isMeta || conversation.channel === 'web' || isEmail;

  // Telling the customer did not work (a closed WhatsApp window, say): the take-over stands, and the agent knows.
  const reportNotice = (r: { notice_sent: boolean; notice_error: string | null }) => {
    if (!r.notice_sent && r.notice_error) notifyError(t('inbox.takeover.noticeFailed', { error: r.notice_error }), t('inbox.title'));
  };
  const takeOver = useMutation({
    mutationFn: () => inbox.takeOverConversation(conversation.id, myName),
    onSuccess: (res) => {
      reportNotice(res.data);
      notifySuccess(t('inbox.takeover.done'), t('inbox.title'));
      refreshDetail();
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.takeover.failed'), t('inbox.title'))
  });
  const handBack = useMutation({
    mutationFn: () => inbox.handBackConversation(conversation.id),
    onSuccess: (res) => {
      reportNotice(res.data);
      notifySuccess(t('inbox.takeover.handedBack', { bot: res.data.taken_over_from ?? takenOverFrom ?? t('inbox.takeover.theBot') }), t('inbox.title'));
      refreshDetail();
      qc.invalidateQueries({ queryKey: ['conversations'] });
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.takeover.failed'), t('inbox.title'))
  });

  const send = useMutation<{ metering_failed?: boolean }, unknown, string, { key: string; text: string }>({
    mutationFn: async (text: string) => {
      // Sending your first reply takes the chat over: the customer hears who has joined before your words,
      // and the bot never talks over you (owner's decision of 4 October 2026).
      // Email has no bot to take over from, so a reply there is only a reply.
      if (!takenByMe && !isEmail) {
        const r = await inbox.takeOverConversation(conversation.id, myName);
        reportNotice(r.data);
        refreshDetail();
      }
      if (viaInbox) {
        const res = await inbox.replyToConversation(conversation.id, text);
        return { metering_failed: res.data.metering_failed };
      }
      await channelWa.sendMessage({
        number_id: conversation.phone_number_id!,
        to: conversation.contact_msisdn!,
        type: 'text',
        text: { body: text },
        // §4 item 15: a retry after a timeout used to send twice.
        idempotency_key: crypto.randomUUID()
      });
      return {};
    },
    onMutate: (text: string) => {
      // §4 item 14: the outbound row reaches the inbox asynchronously, so the
      // refetch almost always lands before it and the reply vanished for a
      // beat. Show it immediately and reconcile when the real one arrives.
      const key = `pending-${Date.now()}`;
      setPending((p) => [...p, { key, body: text }]);
      setReply('');
      return { key, text };
    },
    onSuccess: (data, _text, ctx) => {
      // The message went out and the ledger did not take it: the customer has
      // been served and nothing was charged. Rare, and never swallowed.
      if (data?.metering_failed) notifyError(t('inbox.send.notMetered'), t('inbox.title'));
      qc.invalidateQueries({ queryKey: ['conversation', conversation.id, 'messages'] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
      // Held briefly so the optimistic bubble does not blink out before the
      // server's copy has arrived to replace it.
      setTimeout(() => setPending((p) => p.filter((m) => m.key !== ctx?.key)), 1500);
    },
    onError: (e, text, ctx) => {
      const message = sendErrorMessage(e, t);
      notifyError(message, t('inbox.title'));
      setPending((p) => p.map((m) => m.key === ctx?.key
        ? { ...m, failed: message, onRetry: () => { setPending((q) => q.filter((x) => x.key !== m.key)); send.mutate(text); } }
        : m));
    }
  });

  const assign = useMutation({
    mutationFn: (agentUserId: string | null) => inbox.assignConversation(conversation.id, agentUserId),
    onSuccess: (_r, agentUserId) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      // Name the person, rather than "Assignment updated."
      const name = agents.find((a) => a.user_id === agentUserId)?.label;
      notifySuccess(
        name ? t('inbox.assignedTo', { name }) : t('inbox.unassignedToast'),
        t('inbox.title')
      );
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.assignFailed'), t('inbox.title'))
  });

  // The thread's team (7 October 2026). Read from the detail first, which refreshes on its own, so a
  // team set by the bot's hand-over shows without reselecting the conversation.
  const teams = useTeams();
  const team = (detail.data?.data.team_id !== undefined ? detail.data.data.team_id : conversation.team_id) ?? null;
  const setTeam = useMutation({
    mutationFn: (teamId: string | null) => inbox.setConversationTeam(conversation.id, teamId),
    onSuccess: (_r, teamId) => {
      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['conversation', conversation.id, 'detail'] });
      const name = teams.find((x) => x.id === teamId)?.name;
      notifySuccess(name ? t('inbox.team.moved', { name }) : t('inbox.team.cleared'), t('inbox.title'));
    },
    onError: () => notifyError(t('inbox.team.failed'), t('inbox.title'))
  });

  const acquireLock = useMutation({
    mutationFn: () => inbox.lockConversation(conversation.id, holderName),
    onSuccess: () => { notifySuccess(t('inbox.locked'), t('inbox.title')); refreshDetail(); },
    onError: (e) => { notifyError(lockErrorMessage(e, t), t('inbox.title')); refreshDetail(); }
  });
  const blockMutation = useMutation({
    mutationFn: (opts: { reason?: string; also_on_whatsapp?: boolean }) => inbox.blockContact(conversation.id, opts),
    onSuccess: (res) => {
      setBlockOpen(false);
      const r = res.data;
      // The platform block holds even when WhatsApp refused its part; say which.
      if (r.whatsapp_error) notifyError(t('inbox.block.whatsappFailed', { error: r.whatsapp_error }), t('inbox.title'));
      else notifySuccess(r.blocked_on_whatsapp ? t('inbox.block.doneWhatsapp') : t('inbox.block.done'), t('inbox.title'));
      refreshDetail();
    },
    onError: () => notifyError(t('inbox.block.failed'), t('inbox.title'))
  });
  const unblockMutation = useMutation({
    mutationFn: () => inbox.unblockContact(conversation.id),
    onSuccess: (res) => {
      if (res.data.whatsapp_error) notifyError(t('inbox.block.whatsappFailed', { error: res.data.whatsapp_error }), t('inbox.title'));
      else notifySuccess(t('inbox.block.unblocked'), t('inbox.title'));
      refreshDetail();
    },
    onError: () => notifyError(t('inbox.block.failed'), t('inbox.title'))
  });
  const releaseLock = useMutation({
    mutationFn: () => inbox.unlockConversation(conversation.id),
    onSuccess: () => { notifySuccess(t('inbox.lock.released'), t('inbox.title')); refreshDetail(); },
    onError: (e) => { notifyError(e instanceof Error ? e.message : t('inbox.releaseFailed'), t('inbox.title')); refreshDetail(); }
  });
  useInterval(() => {
    inbox.lockConversation(conversation.id, holderName).catch(() => refreshDetail());
  }, lock?.is_mine ? 15_000 : null);

  const setState = useMutation({
    mutationFn: (state: inbox.ConversationStateCode) => inbox.setConversationState(conversation.id, state),
    onSuccess: (_r, state) => {
      notifySuccess(state === 'resolved' ? t('inbox.resolvedToast') : t('inbox.reopenedToast'), t('inbox.title'));
      refreshDetail();
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('inbox.updateFailed'), t('inbox.title'))
  });

  /** §4 item 21: resolving with unread messages is almost always a mis-click. */
  function resolveOrReopen() {
    if (closed) { setState.mutate('open'); return; }
    if (conversation.unread_count > 0) {
      openConfirmModal({
        title: t('inbox.resolve.confirm.title'),
        children: <Text role="bodySmall">{t('inbox.resolve.confirm.body', { count: conversation.unread_count })}</Text>,
        labels: { confirm: t('inbox.resolve'), cancel: t('action.cancel') },
        onConfirm: () => setState.mutate('resolved')
      });
      return;
    }
    setState.mutate('resolved');
  }

  /**
   * §4 item 6: this used to REPLACE the whole draft with the canned body, so
   * "Thanks! /closing" lost the "Thanks!". The trigger token is swapped for the
   * body and the caret lands after it.
   */
  function insertCanned(r: CannedReply) {
    // Picked from the sidebar there is no "/" to swap, so the reply goes after
    // whatever is already typed rather than over it.
    const next = slashMatch
      ? reply.slice(0, slashMatch.index) + (slashMatch.index > 0 ? ' ' : '') + r.body
      : reply.trim() ? `${reply.replace(/\s+$/, '')} ${r.body}` : r.body;
    setReply(next);
    setDismissedFor(null);
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    });
  }

  /**
   * The picker only ever opened from a typed "/", which nobody discovers. The
   * toolbar button types it for you, at the caret, so the same code path runs
   * and the shortcut is learned by watching it happen.
   */
  function openCanned() {
    const el = composerRef.current;
    const at = el ? el.selectionStart : reply.length;
    const needsSpace = at > 0 && !/\s$/.test(reply.slice(0, at));
    const insert = `${needsSpace ? ' ' : ''}/`;
    setReply(reply.slice(0, at) + insert + reply.slice(at));
    setDismissedFor(null);
    requestAnimationFrame(() => {
      const node = composerRef.current;
      if (!node) return;
      node.focus();
      const caret = at + insert.length;
      node.setSelectionRange(caret, caret);
    });
  }

  function submit() {
    const text = reply.trim();
    if (text && !send.isPending && !lockedByOther && !windowClosed && !blocked) send.mutate(text);
  }

  function onComposerKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (pickerOpen) {
      if (e.key === 'Escape') { e.preventDefault(); setDismissedFor(reply); return; }
      if (matches.length > 0) {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % matches.length); return; }
        if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i - 1 + matches.length) % matches.length); return; }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const r = matches[activeIdx] ?? matches[0];
          if (r) insertCanned(r);
          return;
        }
      }
      /*
       * §4 item 2: with no matches, Enter used to fall through and SEND the
       * trigger. Typing "/refnud" and pressing Enter posted the literal string
       * "/refnud" to the customer. The picker now swallows Enter whether or not
       * it has anything to offer.
       */
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  }

  const assignee = conversation.assigned_agent_user_id ?? null;
  const assignData = [
    { value: '', label: t('inbox.unassigned') },
    ...agents.map((a) => ({ value: a.user_id, label: a.label }))
  ];
  if (assignee && !agents.some((a) => a.user_id === assignee)) {
    // §4 item 28: this used to offer `shortId(assignee)` as a person's name.
    assignData.push({ value: assignee, label: t('inbox.formerAgent') });
  }

  const teamData = teams.length === 0 ? [] : [
    { value: '', label: t('inbox.team.none') },
    ...teams.map((x) => ({ value: x.id, label: x.name }))
  ];
  if (teams.length > 0 && team && !teams.some((x) => x.id === team)) {
    teamData.push({ value: team, label: t('inbox.team.former') });
  }

  function toggleSidebar() {
    if (!docked) { setDrawerOpen((v) => !v); return; }
    // The message details share the column, so asking for the settings while
    // they are open swaps one for the other rather than closing both.
    if (detailsFor) { setDetailsForId(null); setSidebarOpen(true); return; }
    setSidebarOpen((v) => !v);
  }

  /** The contact's name in the header opens the sidebar at the contact section. */
  function openContact() {
    setDetailsForId(null);
    setSectionState((prev) => ({ ...prev, contact: true }));
    if (docked) setSidebarOpen(true);
    else setDrawerOpen(true);
    requestAnimationFrame(() => {
      document.getElementById(sidebarSectionId('contact'))?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  const sidebar = (hideHeader: boolean) => (
    <ConversationSidebar
      id={sidebarId}
      conversationId={conversation.id}
      contactRef={conversation.contact_msisdn ?? conversation.contact_ref ?? ''}
      displayName={conversation.contact_display_name}
      assignee={assignee}
      assignData={assignData}
      onAssign={(id) => assign.mutate(id)}
      team={team}
      teamData={teamData}
      onTeam={(id) => setTeam.mutate(id)}
      teamPending={setTeam.isPending}
      spend={spend}
      onInsertCanned={insertCanned}
      repliesDisabled={lockedByOther || windowClosed || blocked}
      sections={sections}
      onToggleSection={toggleSection}
      onClose={() => (docked ? setSidebarOpen(false) : setDrawerOpen(false))}
      hideHeader={hideHeader}
      bot={{ paused: botPaused, resumesAt: botResumesAt }}
      // The same rule as the header's button: no take-over on a closed conversation, a call or an email.
      botControl={!closed && conversation.channel !== 'voice' && !isEmail}
      onBotPaused={(paused) => (paused ? takeOver.mutate() : handBack.mutate())}
      botPending={takeOver.isPending || handBack.isPending}
      block={{ blocked, since: blockInfo?.blocked_at ?? null, onWhatsapp: blockInfo?.blocked_on_whatsapp ?? false, reason: blockInfo?.reason ?? null }}
      onBlock={() => setBlockOpen(true)}
      onUnblock={() => unblockMutation.mutate()}
      blockPending={blockMutation.isPending || unblockMutation.isPending}
    />
  );

  /*
   * The details panel.
   *
   * The question it answers is always "what happened to THAT message?", so the
   * message has to stay on screen while it is answered; an overlay would put
   * the conversation behind the answer about it. Below 900px there is no room
   * for a second column (the list and the thread already take turns there), so
   * the same panel goes into a drawer.
   */
  const detailsPanel = (hideHeader: boolean) => detailsFor ? (
    <MessageDetails
      key={detailsFor.id}
      conversationId={conversation.id}
      message={detailsFor}
      timezone={timezone}
      onClose={() => setDetailsForId(null)}
      hideHeader={hideHeader}
      metaPayer={metaPayer}
    />
  ) : null;

  /*
   * The column beside the thread holds one thing at a time. The message
   * details win while they are open, because the agent has just asked for
   * them; closing them brings the settings back.
   */
  const sideColumn = !narrow && detailsFor
    ? detailsPanel(false)
    : docked && sidebarOpen ? sidebar(false) : null;
  const sidebarShowing = docked ? sidebarOpen && !detailsFor : drawerOpen;

  return (
    <>
    <div className="rr-inbox2__pane">
      <ThreadHeader
        conversation={conversation}
        sla={detail.data?.data.sla ?? conversation.sla}
        now={now}
        sessionWindow={sessionWindow}
        stateCode={stateCode}
        closed={closed}
        onTransfer={() => openTransferModal({
          conversationId: conversation.id,
          currentAssignee: assignee,
          title: t('inbox.transfer.title')
        })}
        onResolve={resolveOrReopen}
        onLock={() => acquireLock.mutate()}
        lockDisabled={lockedByOther}
        lockHidden={!!lock?.is_mine}
        resolving={setState.isPending}
        locking={acquireLock.isPending}
        onOpenContact={openContact}
        onBack={narrow ? onBack : undefined}
        sidebarOpen={sidebarShowing}
        onToggleSidebar={toggleSidebar}
        sidebarId={sidebarId}
        botPaused={botPaused}
        botResumesAt={botResumesAt}
        blocked={blocked}
        onBook={() => setBookOpen(true)}
        onCreateTicket={() => setTicketOpen(true)}
        linkedTicket={linkedTicket}
        // No take-over on a record of calls, nor on email, which has no bot.
        takeover={conversation.channel === 'voice' || isEmail ? null : {
          byMe: takenByMe,
          name: takenOverBy ? (takenByMe ? t('inbox.takeover.you') : agents.find((a) => a.user_id === takenOverBy)?.label ?? t('inbox.takeover.someone')) : null,
          from: takenOverFrom,
          pending: takeOver.isPending || handBack.isPending,
          onTakeOver: () => takeOver.mutate(),
          onHandBack: () => handBack.mutate()
        }}
      />

      <LockIndicator
        // A lock taken before names were kept still names its holder from the team list.
        lock={lock && !lock.holder_name ? { ...lock, holder_name: agents.find((a) => a.user_id === lock.holder_user_id)?.label ?? null } : lock}
        onRelease={() => releaseLock.mutate()}
        releasing={releaseLock.isPending}
      />

      <MessageThread
        messages={messages}
        loading={loading}
        error={error}
        timezone={timezone}
        pending={pending}
        /* Only the failure the platform cannot name is offered a retry: see
           `deliveryFailure` in sdk-api. Sending the same plain text again
           after the 24-hour window closed, or while the customer's WhatsApp
           account has no payment method, fails identically. */
        onRetry={(body) => send.mutate(body)}
        onOpenDetails={(m) => setDetailsForId((current) => (current === m.id ? null : m.id))}
        detailsForId={detailsForId}
        channel={conversation.channel}
        threadSubject={conversation.subject ?? null}
        metaPayer={metaPayer}
      />

      <div className="rr-inbox2__footer" data-guide="inbox-footer">
        {blocked ? (
          <div className="rr-inbox2__blocked-bar" role="status">
            <Text role="bodySmall" className="rr-inbox2__blocked-text">{t('inbox.block.bar')}</Text>
            <Button size="xs" variant="default" loading={unblockMutation.isPending} onClick={() => unblockMutation.mutate()}>
              {t('inbox.block.unblock')}
            </Button>
          </div>
        ) : conversation.channel === 'voice' ? (
          /* A record of phone calls (2 October 2026): nothing can be sent on it. Say so, and how to answer instead. */
          <div className="rr-inbox2__window-closed" role="status" data-testid="inbox-voice-readonly">
            <div className="rr-inbox2__window-closed-text">
              <Text role="bodySmall" fw={600}>{t('inbox.voice.readOnly.title')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('inbox.voice.readOnly.body')}</Text>
            </div>
          </div>
        ) : pageBroken ? (
          <div className="rr-inbox2__window-closed rr-inbox2__page-broken" role="status" data-testid="inbox-page-broken">
            <div className="rr-inbox2__window-closed-text">
              <Text role="bodySmall" fw={600}>{t('inbox.pageBroken.title')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {t(metaAccount?.via === 'manychat' ? 'inbox.pageBroken.manychat' : 'inbox.pageBroken.body')}
              </Text>
            </div>
            <Button size="xs" variant="light" color="red" className="rr-inbox2__window-closed-action" onClick={() => navigate('/channels')}>
              {t('inbox.pageBroken.action')}
            </Button>
          </div>
        ) : windowClosed ? (
          /* Rather than a disabled box with no explanation, say what happened
             and where the reply now has to come from, in the space the reply
             box would take and no more.

             The way out is not the same on every channel, and offering one
             that does not exist would be worse than saying so. On WhatsApp an
             approved template still reaches somebody outside the window. On
             Messenger and Instagram nothing does: Meta withdrew three of its
             four message tags on 27 April 2026 and the one that remains needs
             its own App Review. So those two are told the truth, which is to
             wait for the person to write again. */
          <div className="rr-inbox2__window-closed" role="status">
            <span className="rr-inbox2__window-closed-icon" aria-hidden><ClockIcon size={18} /></span>
            <div className="rr-inbox2__window-closed-text">
              <Text role="bodySmall" fw={600}>{t('inbox.window.closed.title')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">
                {isMeta
                  ? t(`inbox.window.closed.body.${conversation.channel === 'instagram' ? 'instagram' : 'messenger'}`)
                  : t('inbox.window.closed.body')}
              </Text>
            </div>
            {!isMeta && (
              <Button size="xs" variant="light" className="rr-inbox2__window-closed-action" onClick={() => navigate('/templates')}>
                {t('inbox.window.closed.action')}
              </Button>
            )}
          </div>
        ) : (
          <Composer
            value={reply}
            onChange={setReply}
            onSubmit={submit}
            onKeyDown={onComposerKeyDown}
            textareaRef={composerRef}
            sending={send.isPending}
            disabled={lockedByOther}
            onOpenCanned={openCanned}
            pickerListId={pickerOpen && matches.length ? listId : undefined}
            activeOptionId={pickerOpen && matches[activeIdx] ? `${listId}-${activeIdx}` : undefined}
            picker={pickerOpen ? (
              <CannedReplyPicker
                listId={listId}
                matches={matches}
                loading={pickerLoading}
                activeIndex={activeIdx}
                onHover={setActiveIdx}
                onSelect={insertCanned}
              />
            ) : undefined}
          />
        )}
      </div>

      <NewBookingModal
        opened={bookOpen}
        onClose={() => setBookOpen(false)}
        tz={orgTimezone}
        prefill={{
          contact: {
            name: conversation.contact_display_name ?? undefined,
            phone: conversation.channel === 'email' ? undefined : conversation.contact_msisdn ?? undefined,
            email: isEmail ? conversation.contact_ref : undefined,
            ref: conversation.contact_ref
          },
          conversationId: conversation.id,
          channel: conversation.channel ?? 'whatsapp'
        }}
        onBooked={(b) => notifySuccess(t('inbox.book.done', { number: b.number, when: b.when_text }), t('inbox.book'))}
      />

      <CreateTicketModal
        opened={ticketOpen}
        onClose={() => setTicketOpen(false)}
        prefill={{
          subject: ticketSubjectFrom(messages),
          conversationId: conversation.id,
          channel: conversation.channel ?? 'whatsapp',
          contact: { ref: conversation.contact_ref, name: conversation.contact_display_name ?? undefined }
        }}
        onCreated={(tk) => {
          qc.invalidateQueries({ queryKey: linkedTicketKey(conversation.id) });
          notifySuccess(t('inbox.ticket.created', { number: tk.number }), t('inbox.ticket.create'));
        }}
      />

      <BlockCustomerModal
        opened={blockOpen}
        onClose={() => setBlockOpen(false)}
        whatsapp={conversation.channel === 'whatsapp'}
        pending={blockMutation.isPending}
        onConfirm={(opts) => blockMutation.mutate(opts)}
      />

      {!docked && (
        <Drawer
          opened={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t('inbox.sidebar.title')}
          position="right"
        >
          {sidebar(true)}
        </Drawer>
      )}

      {narrow && (
        <Drawer
          opened={!!detailsFor}
          onClose={() => setDetailsForId(null)}
          title={t('inbox.details.title')}
          position="right"
        >
          {detailsPanel(true)}
        </Drawer>
      )}
    </div>
    {sideColumn}
    </>
  );
}
